'use client';

import dynamic from 'next/dynamic';
import type { StaticImageData } from 'next/image';
import { useSearchParams } from 'next/navigation';
import { useState } from 'react';
import type { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import connectDeviceQrCode from '@alltrails/layout/components/AppQrCodeModal/qrCodes/connect-device-qr-code.svg';
import exploreQrCode from '@alltrails/layout/components/AppQrCodeModal/qrCodes/explore-qr-code.webp';
import navigatorQrCode from '@alltrails/layout/components/AppQrCodeModal/qrCodes/navigator-qr-code.svg';

const AppQrCodeModal = dynamic(() => import('@alltrails/layout/components/AppQrCodeModal'), { ssr: false });

type MarketingModalConfig = {
  param: string;
  qrCode: string | StaticImageData;
  subtitleMobile?: ReactNode;
  title: ReactNode;
};

const MARKETING_MODAL_CONFIGS: MarketingModalConfig[] = [
  {
    param: 'collections_desktop',
    qrCode: exploreQrCode,
    title: <FormattedMessage defaultMessage="Download the app to access our new Collections" />
  },
  {
    param: 'device_connect',
    qrCode: connectDeviceQrCode,
    title: <FormattedMessage defaultMessage="Download the app to connect your devices" />
  },
  {
    param: 'navigator_desktop',
    qrCode: navigatorQrCode,
    subtitleMobile: <FormattedMessage defaultMessage="Navigator is only for iOS or Android." />,
    title: <FormattedMessage defaultMessage="Download the AllTrails app to use Navigator" />
  },
  {
    param: 'mobile_only',
    qrCode: exploreQrCode,
    title: <FormattedMessage defaultMessage="This feature is only available on the mobile app" />
  }
];

const MarketingModals = () => {
  const searchParams = useSearchParams();
  const currentQueryString = searchParams.toString();
  const [closedModalQueryString, setClosedModalQueryString] = useState<string | null>(null);

  if (closedModalQueryString === currentQueryString) {
    return null;
  }

  const modalConfig = MARKETING_MODAL_CONFIGS.find(({ param }) => searchParams.has(param));

  if (!modalConfig) {
    return null;
  }

  const onRequestClose = () => setClosedModalQueryString(currentQueryString);

  return (
    <AppQrCodeModal
      isOpen
      onRequestClose={onRequestClose}
      qrCode={modalConfig.qrCode}
      subtitleMobile={modalConfig.subtitleMobile}
      title={modalConfig.title}
    />
  );
};

export default MarketingModals;
